import SectionHero4 from "designs/DesignB/components/SectionHero/SectionHero";
import { heroBandRgb } from "utils/heroBand";
import { clearRenterSession } from "utils/renterSession";
import SiteHeader from "../SiteHeader";
import SectionGridFeaturePlaces from "./SectionGridFeaturePlaces";
import { useEffect, useState } from "react";
import MobileSearch from "designs/DesignB/components/MobileSearchForm/MobileSearch";
import SectionTestimonials from "designs/DesignB/components/SectionTestimonials/SectionTestimonials";
import ImageSection from "designs/DesignB/components/ImageSection/ImageSection";
import { useData } from "data/data-provider";
// Use the Common HeroSearchForm wrapper (same one Design A uses) so the
// search bar behaves identically across both designs — date / time / location
// popovers all work the same way. Design B styling is applied to the
// surrounding container, NOT the form itself, to avoid breaking the picker
// interactions.
import HeroSearchForm from "designs/Common/HeroSearchForm";
import CarLogo from "designs/DesignB/components/CarLogo/CarLogo";
import WhyChooseUS from "designs/DesignB/components/WhyUs/WhyUs";
import FAQ from "designs/DesignB/components/FAQ/FaqAccordion";
import HomeAboutUs from "designs/DesignB/components/HomeAboutUs/HomeAboutUs";
import GlobalPopUp from "shared/pop-up/global_popUp";
import { isUsableUrl } from "@1-now/booking-core";
import { storageGet, storageSet } from "utils/safeStorage";
import { landedFromAd } from "utils/landingSource";
import { aboutShown, faqsShown, testimonialsShown, whyUsShown } from "utils/homeSections";

// Maps a section's Wix-style vertical-spacing preset (Section.vertical_spacing,
// chosen in the FMS) to the wrapper's top-margin classes — i.e. how much
// breathing room sits above the section. Anything unset falls back to the
// original "normal" rhythm so existing sites are visually unchanged.
const sectionSpacing = (preset?: string | null) => {
  switch (preset) {
    case "compact":
      return "mt-6 md:mt-8";
    case "spacious":
      return "mt-16 md:mt-28";
    default:
      return "mt-10 md:mt-16"; // "normal" / unset
  }
};

// A section is shown unless the admin explicitly toggled it off in the FMS.
// `is_visible` is only ever `false` when hidden; null/undefined (older rows
// or sections that never had the field) read as visible.
const isShown = (section: any) => section?.is_visible !== false;

function PageHome() {
  const { banner, whyUs, testimonials, popUp, faqs, aboutUs, imageSection, companyDetails } =
    useData();
  const [showPopUp, setShowPopUp] = useState(false);
  // Treat literal "undefined" / "null" strings as missing so we never render
  // `background-image: url(undefined)` (which 404s — ONENOW-457).
  const desktopBannerBg = isUsableUrl(banner?.image_url) ? banner.image_url : "";
  const mobileBannerBgCandidate =
    banner?.mobile_image_url || banner?.image_url;
  const mobileBannerBg = isUsableUrl(mobileBannerBgCandidate)
    ? mobileBannerBgCandidate
    : "";

  // The band the heading sits on, in a colour the heading reads on (1NOW-11175), and the side
  // the FMS put the text on: the band follows it.
  const heroBand = heroBandRgb(companyDetails?.banner_heading_color, companyDetails?.button_color);
  const heroAlign: "left" | "center" | "right" =
    banner?.alignment === "center" || banner?.alignment === "right" ? banner.alignment : "left";

  useEffect(() => {
    // Landing back on the home page means the booking flow is over, so the
    // renter credential is dropped here (1NOW-1557). Also purges anything a
    // previous build left in localStorage.
    clearRenterSession();
  }, []);

  useEffect(() => {
    // At most once per visit, and never for a visitor who arrived from an ad (1NOW-11115).
    // A suppressed pop-up is not marked as shown.
    if (popUp && popUp?.heading && popUp?.description && !landedFromAd()) {
      const hasShown = storageGet("session", "hasShownWelcomePopup");
      if (!hasShown) {
        setShowPopUp(true);
        storageSet("session", "hasShownWelcomePopup", "true");
      }
    }
  }, [popUp]);

  const handleClosePopUp = () => {
    setShowPopUp(false);
    storageSet("session", "hasShownWelcomePopup", "true");
  };

  return (
    <>
      <div className="nc-PageHome3 relative">
        {/* The banner: the header and the hero band. The header scrolls away with the band;
            once the band is gone, the pinned copy (PinnedHeader, in DesignBRouter) takes over:
            with the menu at first, and with the search bar once the search form below has gone
            too (MainNav decides that) (1NOW-11153). */}
        <div data-topbar-banner>
          <SiteHeader />
          <div
            className="db-hero"
            data-align={heroAlign}
            style={{ ["--db-hero-band" as any]: heroBand }}
          >
            {/* The photo: the desktop image from lg, the mobile one (else the desktop one)
                below it. No image is no photo: the band colour stands alone. */}
            <div
              aria-hidden="true"
              className="db-hero__photo hidden lg:!block"
              style={{
                backgroundImage: desktopBannerBg ? `url(${desktopBannerBg})` : undefined,
                backgroundSize: banner?.position ?? "cover",
                backgroundPosition: banner?.image_alignment ?? "center",
              }}
            />
            <div
              aria-hidden="true"
              className="db-hero__photo lg:!hidden"
              style={{
                backgroundImage: mobileBannerBg ? `url(${mobileBannerBg})` : undefined,
                backgroundPosition: banner?.image_alignment ?? "center",
              }}
            />
            <div aria-hidden="true" className="db-hero__scrim" />
            <SectionHero4 />
          </div>
        </div>

        {/* The search, inside the hero: a card that straddles the band's bottom edge. The
            desktop and phone forms are both in the tree and one is display:none, so the
            top bar's search (which waits for this one to scroll away) reads the visible one. */}
        <div data-topbar-search className="hidden lg:!block container db-search-pull">
          <div className="db-search">
            <HeroSearchForm />
          </div>
        </div>
        <div data-topbar-search className="block lg:!hidden container db-search-pull">
          <div className="db-search">
            <MobileSearch />
          </div>
        </div>

        <div id="section">
          <div className="db-section">
            <CarLogo />
          </div>

          <div className="container db-section">
            <SectionGridFeaturePlaces />
          </div>
        </div>

        {aboutShown(aboutUs) && (
          <div
            className={`container ${sectionSpacing(aboutUs?.vertical_spacing)}`}
            id="section-about"
          >
            <HomeAboutUs />
          </div>
        )}
        {/* Why us is a full-width band, so it is not wrapped in a container here. */}
        {whyUsShown(whyUs) && (
          <div className={sectionSpacing(whyUs?.vertical_spacing)} id="section-why-choose-us">
            <WhyChooseUS />
          </div>
        )}
        {/* Reviews: only when the tenant has some (testimonialsShown). */}
        {testimonialsShown(testimonials) && (
          <div
            className={`container ${sectionSpacing(testimonials?.vertical_spacing)}`}
            id="section-testimonials"
          >
            <SectionTestimonials />
          </div>
        )}
        {faqsShown(faqs) && (
          <div
            className={`container ${sectionSpacing(faqs?.vertical_spacing)}`}
            id="section-faqs"
          >
            <FAQ />
          </div>
        )}
        {isShown(imageSection) && (
          <div
            className={`container ${sectionSpacing(
              imageSection?.vertical_spacing
            )} mb-4`}
          >
            <ImageSection />
          </div>
        )}
        {popUp && popUp?.heading && popUp?.description && (
          <GlobalPopUp
            onClose={() => {
              handleClosePopUp();
            }}
            isShowModal={showPopUp}
            popUpData={popUp}
          />
        )}
      </div>
    </>
  );
}

export default PageHome;
